import React from "react";
import TextAnswer from "./TextAnswer";
import ShipmentCard from "./ShipmentCard";
import InvoiceCard from "./InvoiceCard";
import OrderCard from "./OrderCard";
import DataTable from "./DataTable";
import EnhancedDataTable from "./EnhancedDataTable";
import GenericCard from "./GenericCard";
import SourceList from "./SourceList";
import { parseResponseData } from "../utils/responseParser";
import { useEntityNavigation } from "../../hooks/useEntityNavigation";

/**
 * ResponseRenderer - Main router for rendering structured AI responses
 *
 * Renders responses using two strategies:
 * 1. Metadata-driven rendering (preferred): Uses backend-provided metadata to select components
 * 2. Heuristics-based rendering (fallback): Uses pattern matching for backward compatibility
 *
 * @param {Object} props
 * @param {Object} props.message - The message object containing answer, data, sources, metadata
 * @returns {JSX.Element}
 */
const ResponseRenderer = ({ message }) => {
    const { content, data, sources, metadata } = message;
    const { openEntity } = useEntityNavigation();

    // Strategy: Use metadata if available, fallback to heuristics for backward compatibility
    const hasMetadata = metadata && metadata.responseTypes;

    /**
     * Handler for row/card clicks - navigates to entity detail view
     */
    const handleEntityClick = (entityData, responseType) => {
        openEntity(entityData, responseType);
    };

    if (hasMetadata) {
        return renderWithMetadata(content, data, sources, metadata, handleEntityClick);
    } else {
        return renderWithHeuristics(content, data, sources, handleEntityClick);
    }
};

/**
 * NEW: Metadata-driven rendering
 * Uses backend-provided metadata to route each tool's data to the appropriate component.
 */
const renderWithMetadata = (content, data, sources, metadata, handleEntityClick) => {
    const responseTypes = metadata.responseTypes || {};
    const cardConfigs = metadata.cardConfigs || {};

    return (
        <div className="response-renderer flex flex-col gap-3">
            {content && <TextAnswer text={content} />}

            {data && Object.entries(data).map(([toolName, toolData]) => {
                const responseType = responseTypes[toolName] || "table";
                const cardConfig = cardConfigs[toolName];
                return <React.Fragment key={toolName}>{renderByType(responseType, toolData, cardConfig, handleEntityClick)}</React.Fragment>;
            })}

            {sources && sources.length > 0 && (
                <div className="pt-2 mt-1 border-t border-gray-100">
                    <SourceList sources={sources} />
                </div>
            )}
        </div>
    );
};

/**
 * OLD: Heuristics-based rendering (backward compatibility)
 * Uses pattern matching to detect data types when metadata is not available.
 */
const renderWithHeuristics = (content, data, sources, handleEntityClick) => {
    const parsedData = parseResponseData(data);

    return (
        <div className="response-renderer flex flex-col gap-3">
            {content && <TextAnswer text={content} />}
            {parsedData.shipment && (
                <ShipmentCard
                    data={parsedData.shipment}
                    onClick={(shipment) => handleEntityClick(shipment, "shipment")}
                />
            )}
            {parsedData.invoice && (
                <InvoiceCard
                    data={parsedData.invoice}
                    onClick={(invoice) => handleEntityClick(invoice, "invoice")}
                />
            )}
            {parsedData.other && Object.keys(parsedData.other).length > 0 && (
                <DataTable data={parsedData.other} title="Additional Information" />
            )}
            {sources && sources.length > 0 && (
                <div className="pt-2 mt-1 border-t border-gray-100">
                    <SourceList sources={sources} />
                </div>
            )}
        </div>
    );
};

/**
 * Route to appropriate component based on response type.
 * Maps backend response type vocabulary to frontend components.
 */
const renderByType = (responseType, data, cardConfig, handleEntityClick) => {
    switch (responseType) {
        // Single entity cards
        case "shipment":
            return (
                <ShipmentCard
                    data={data}
                    onClick={(shipment) => handleEntityClick(shipment, "shipment")}
                />
            );
        case "invoice":
            return (
                <InvoiceCard
                    data={data}
                    onClick={(invoice) => handleEntityClick(invoice, "invoice")}
                />
            );
        case "order":
            return (
                <OrderCard
                    data={data}
                    onClick={(order) => handleEntityClick(order, "order")}
                />
            );

        // List views - use EnhancedDataTable
        case "shipment-list":
            return (
                <EnhancedDataTable
                    data={data}
                    responseType="shipment-list"
                    onRowClick={handleEntityClick}
                />
            );
        case "invoice-list":
            return (
                <EnhancedDataTable
                    data={data}
                    responseType="invoice-list"
                    onRowClick={handleEntityClick}
                />
            );
        case "order-list":
            return (
                <EnhancedDataTable
                    data={data}
                    responseType="order-list"
                    onRowClick={handleEntityClick}
                />
            );

        // Generic views
        case "card":
            return cardConfig ? <GenericCard data={data} config={cardConfig} /> : <DataTable data={data} />;
        case "table":
            return (
                <EnhancedDataTable
                    data={data}
                    responseType="default"
                    onRowClick={handleEntityClick}
                />
            );

        // Default fallback
        default:
            return <DataTable data={data} />;
    }
};

export default ResponseRenderer;
